import React, { useEffect, useMemo, useRef, useState } from 'react';
import { fadeIn } from '../../utils/gsapAnimations';

const ORDERS_PER_PAGE = 5;

const formatCurrency = (value) => {
  const amount = Number(value || 0);

  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    maximumFractionDigits: 0,
  }).format(amount);
};

function OrderHistory({
  orders = [],
  favoriteProducts = [],
  loading = false,
}) {
  const sectionRef = useRef(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [expandedOrders, setExpandedOrders] = useState({});

  useEffect(() => {
    fadeIn(sectionRef.current, 0.3);
  }, []);

  const totalPages = Math.max(
    1,
    Math.ceil(orders.length / ORDERS_PER_PAGE)
  );

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  useEffect(() => {
    setCurrentPage(1);
    setExpandedOrders({});
  }, [orders]);

  const visibleOrders = useMemo(() => {
    const startIndex = (currentPage - 1) * ORDERS_PER_PAGE;

    return orders.slice(
      startIndex,
      startIndex + ORDERS_PER_PAGE
    );
  }, [orders, currentPage]);

  const toggleOrder = (orderId) => {
    setExpandedOrders((previous) => ({
      ...previous,
      [orderId]: !previous[orderId],
    }));
  };

  const goToPreviousPage = () => {
    setCurrentPage((previous) => Math.max(1, previous - 1));
  };

  const goToNextPage = () => {
    setCurrentPage((previous) =>
      Math.min(totalPages, previous + 1)
    );
  };

  return (
    <div ref={sectionRef} className="space-y-8">
      <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-lora font-semibold text-burgundy">
              Recent Orders
            </h2>

            <p className="text-sm text-secondary/70 mt-1">
              Your latest spending activity.
            </p>
          </div>

          {!loading && orders.length > 0 && (
            <div className="text-sm text-secondary/70">
              {orders.length} {orders.length === 1 ? 'order' : 'orders'}
            </div>
          )}
        </div>

        {loading ? (
          <p className="text-secondary">Loading orders...</p>
        ) : orders.length === 0 ? (
          <div className="p-6 rounded-2xl bg-cream/60 text-center">
            <p className="text-burgundy font-medium">
              No orders yet.
            </p>

            <p className="text-sm text-secondary mt-1">
              Your purchases will appear here once you place an order.
            </p>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {visibleOrders.map((order) => {
                const isExpanded = Boolean(expandedOrders[order.id]);
                const itemCount = order.items?.length || 0;

                return (
                  <div
                    key={order.id}
                    className="border border-gray-100 rounded-2xl overflow-hidden hover:border-primary/30 hover:shadow-md transition"
                  >
                    <div className="p-5">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div className="min-w-0">
                          <p className="font-montserrat text-burgundy font-semibold">
                            Order #{order.id}
                          </p>

                          <p className="text-sm text-secondary/70 mt-1">
                            {order.date}
                          </p>

                          {itemCount > 0 && (
                            <p className="text-xs text-secondary/60 mt-1">
                              {itemCount}{' '}
                              {itemCount === 1 ? 'item' : 'items'}
                            </p>
                          )}
                        </div>

                        <div className="flex flex-col sm:flex-row sm:items-center gap-4 md:justify-end">
                          <div className="text-left sm:text-right">
                            <p className="text-xl font-lora font-semibold text-primary">
                              {formatCurrency(order.amount)}
                            </p>

                            <p className="text-xs text-secondary/70 capitalize">
                              {order.status} • {order.payment_status}
                            </p>
                          </div>

                          {itemCount > 0 && (
                            <button
                              type="button"
                              onClick={() => toggleOrder(order.id)}
                              className="self-start sm:self-center px-4 py-2 rounded-full border border-primary/30 text-primary text-sm font-medium hover:bg-primary hover:text-white transition"
                              aria-expanded={isExpanded}
                            >
                              {isExpanded ? 'Hide Items' : 'View Items'}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {isExpanded && itemCount > 0 && (
                      <div className="border-t border-gray-100 bg-cream/30 px-5 py-4">
                        <div className="space-y-3">
                          {order.items.map((item, index) => (
                            <div
                              key={`${order.id}-${item.product_name}-${index}`}
                              className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-4 text-sm"
                            >
                              <span className="text-secondary break-words">
                                {item.product_name} × {item.qty}
                              </span>

                              <span className="font-medium text-burgundy whitespace-nowrap">
                                {formatCurrency(item.total)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {totalPages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 pt-5 border-t border-gray-100">
                <p className="text-sm text-secondary/70">
                  Showing{' '}
                  {(currentPage - 1) * ORDERS_PER_PAGE + 1}–
                  {Math.min(
                    currentPage * ORDERS_PER_PAGE,
                    orders.length
                  )}{' '}
                  of {orders.length}
                </p>

                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={goToPreviousPage}
                    disabled={currentPage === 1}
                    className="px-4 py-2 rounded-full border border-gray-200 text-sm font-medium text-burgundy hover:border-primary hover:text-primary disabled:opacity-40 disabled:cursor-not-allowed transition"
                  >
                    Previous
                  </button>

                  <span className="text-sm text-secondary">
                    Page {currentPage} of {totalPages}
                  </span>

                  <button
                    type="button"
                    onClick={goToNextPage}
                    disabled={currentPage === totalPages}
                    className="px-4 py-2 rounded-full border border-gray-200 text-sm font-medium text-burgundy hover:border-primary hover:text-primary disabled:opacity-40 disabled:cursor-not-allowed transition"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-6">
        <h2 className="text-2xl font-lora font-semibold text-burgundy">
          What You Spend On Most
        </h2>

        <p className="text-sm text-secondary/70 mt-1 mb-6">
          Based on your product spending pattern.
        </p>

        {loading ? (
          <p className="text-secondary">Analyzing preferences...</p>
        ) : favoriteProducts.length === 0 ? (
          <div className="p-6 rounded-2xl bg-cream/60 text-center">
            <p className="text-burgundy font-medium">
              No preference data yet.
            </p>

            <p className="text-sm text-secondary mt-1">
              We’ll show your favorite products after more purchases.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {favoriteProducts.map((product, index) => {
              const maxAmount = favoriteProducts[0]?.amount || 1;

              const width = Math.min(
                100,
                Math.round(
                  (Number(product.amount || 0) /
                    Number(maxAmount || 1)) *
                    100
                )
              );

              return (
                <div key={`${product.product_name}-${index}`}>
                  <div className="flex justify-between gap-4 text-sm mb-2">
                    <span className="font-medium text-burgundy break-words">
                      {index + 1}. {product.product_name}
                    </span>

                    <span className="text-secondary whitespace-nowrap">
                      {formatCurrency(product.amount)}
                    </span>
                  </div>

                  <div className="w-full h-3 bg-cream rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary rounded-full"
                      style={{ width: `${width}%` }}
                    />
                  </div>

                  <p className="text-xs text-secondary/70 mt-1">
                    Quantity bought: {product.quantity}
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default OrderHistory;